// src/main.js

import { createApp } from "vue";

import App from "./App.vue";
import router from "./router/index.js";

import "materialize-css/dist/css/materialize.min.css";
import "@/assets/coredeck-ui.css";
import "@/assets/mailer-ui.css";

import M from "materialize-css";

window.M = M;

import {
  auth,
  authReady,
} from "@/firebase";

import {
  onAuthStateChanged,
} from "firebase/auth";

import {
  useUserRole,
} from "@/scripts/useScripts/useUserRole";

import {
  startInactivityTimer,
  stopInactivityTimer,
} from "@/scripts/logout";

import {
  useVenueContext,
  waitForVenueReady,
} from "@/scripts/useScripts/useVenueContext";

import {
  installMailerRoute,
} from "@/scripts/mailer/installMailerRoute";

installMailerRoute(router);

const {
  loadUserRole,
} = useUserRole();

const venueCtx =
  useVenueContext();

/* =========================================================
   Helpers
   ========================================================= */

function clean(v) {
  return String(
    v ?? ""
  ).trim();
}

function normalizeDate(v) {
  const value =
    clean(v);

  if (
    !value ||
    value === "-" ||
    value === "—"
  ) {
    return "";
  }

  return value;
}

/**
 * Persist and broadcast venue/date context.
 */
function publishVenueContext({
  venueId,
  tradingDate,
  weekEnding,
}) {
  const vid =
    clean(venueId);

  const td =
    normalizeDate(
      tradingDate
    );

  const we =
    clean(
      weekEnding
    );

  if (vid) {
    localStorage.setItem(
      "venue",
      vid
    );
  } else {
    localStorage.removeItem(
      "venue"
    );
  }

  if (td) {
    localStorage.setItem(
      "tradingDate",
      td
    );

    localStorage.setItem(
      "businessDate",
      td
    );
  } else {
    localStorage.removeItem(
      "tradingDate"
    );

    localStorage.removeItem(
      "businessDate"
    );
  }

  if (we) {
    localStorage.setItem(
      "weekEnding",
      we
    );
  } else {
    localStorage.removeItem(
      "weekEnding"
    );
  }

  const detail = {
    venueId:
      vid,

    businessDate:
      td,

    tradingDate:
      td,

    weekEnding:
      we,

    weekEndingDate:
      we,
  };

  try {
    window.dispatchEvent(
      new CustomEvent(
        "venue-context-updated",
        {
          detail,
        }
      )
    );
  } catch {
    // no-op
  }

  try {
    document.dispatchEvent(
      new CustomEvent(
        "venue-context-updated",
        {
          detail,
        }
      )
    );
  } catch {
    // no-op
  }
}

/* =========================================================
   Authentication lifecycle
   ========================================================= */

onAuthStateChanged(
  auth,
  async (user) => {
    if (!user) {
      stopInactivityTimer();

      try {
        localStorage.removeItem(
          "venue"
        );

        localStorage.removeItem(
          "tradingDate"
        );

        localStorage.removeItem(
          "businessDate"
        );

        localStorage.removeItem(
          "weekEnding"
        );
      } catch {
        // no-op
      }

      return;
    }

    await authReady;

    startInactivityTimer();

    try {
      await loadUserRole();
    } catch (e) {
      console.warn(
        "[ROLE] loadUserRole failed:",
        e
      );
    }

    try {
      await waitForVenueReady({
        timeoutMs: 12000,
      });

      const venueId =
        venueCtx.venueId.value ||
        "";

      const tradingDate =
        venueCtx.tradingDate.value ||
        "";

      const weekEnding =
        venueCtx.weekEnding.value ||
        "";

      publishVenueContext({
        venueId,
        tradingDate,
        weekEnding,
      });
    } catch (e) {
      console.warn(
        "[VENUE] not ready yet:",
        e?.message ||
          e
      );
    }
  }
);

/* =========================================================
   App mount
   ========================================================= */

(async () => {
  await authReady;

  const app =
    createApp(App);

  /* =======================================================
     Materialize tooltip directive
     ======================================================= */

  app.directive(
    "mtooltip",
    {
      mounted(
        el,
        binding
      ) {
        try {
          const text =
            String(
              binding.value ??
                ""
            ).trim();

          el.setAttribute(
            "data-tooltip",
            text
          );

          el.classList.add(
            "tooltipped"
          );

          const position =
            String(
              binding.arg ||
                "top"
            ).trim() ||
            "top";

          el.setAttribute(
            "data-position",
            position
          );

          const delay =
            binding.modifiers &&
            typeof binding.modifiers ===
              "object" &&
            binding.modifiers.delay
              ? 100
              : 0;

          el._mtInstance =
            M.Tooltip.init(
              el,
              {
                enterDelay:
                  delay,
              }
            );
        } catch {
          // no-op
        }
      },

      updated(
        el,
        binding
      ) {
        try {
          const nextText =
            String(
              binding.value ??
                ""
            ).trim();

          const currentText =
            String(
              el.getAttribute(
                "data-tooltip"
              ) ??
                ""
            ).trim();

          const nextPosition =
            String(
              binding.arg ||
                "top"
            ).trim() ||
            "top";

          const currentPosition =
            String(
              el.getAttribute(
                "data-position"
              ) ??
                ""
            ).trim();

          if (
            nextText !==
              currentText ||
            nextPosition !==
              currentPosition
          ) {
            if (
              el._mtInstance &&
              typeof el
                ._mtInstance
                .destroy ===
                "function"
            ) {
              el._mtInstance.destroy();
            }

            el.setAttribute(
              "data-tooltip",
              nextText
            );

            el.setAttribute(
              "data-position",
              nextPosition
            );

            el.classList.add(
              "tooltipped"
            );

            const delay =
              binding.modifiers &&
              typeof binding.modifiers ===
                "object" &&
              binding.modifiers.delay
                ? 100
                : 0;

            el._mtInstance =
              M.Tooltip.init(
                el,
                {
                  enterDelay:
                    delay,
                }
              );
          }
        } catch {
          // no-op
        }
      },

      unmounted(el) {
        try {
          if (
            el._mtInstance &&
            typeof el
              ._mtInstance
              .destroy ===
              "function"
          ) {
            el._mtInstance.destroy();
          }

          delete el._mtInstance;
        } catch {
          // no-op
        }
      },
    }
  );

  app.use(router);

  await router.isReady();

  app.mount("#app");
})();